import React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { WORKSPACE_LIST_PAGE } from "@/lib/constants/workspace-layout";
import { LIST_TABLE_CELL, LIST_TABLE_TH, LIST_TABLE_THEAD_ROW_MUTED } from "@/lib/constants/workspace-styles";
import { cn } from "@/lib/utils";

const DEFAULT_ROW_COUNT = 8;
const DEFAULT_COLUMN_COUNT = 4;

export type ListTableSkeletonProps = {
  rows?: number;
  columns?: number;
  className?: string;
  testId?: string;
};

/** Table-body skeleton matching hub list geometry (header + rows). Page `table` slot when loading. */
export function ListTableSkeleton({
  rows = DEFAULT_ROW_COUNT,
  columns = DEFAULT_COLUMN_COUNT,
  className,
  testId = "list-table-skeleton",
}: ListTableSkeletonProps) {
  const safeRows = Math.max(1, rows);
  const safeColumns = Math.max(2, columns);
  return (
    <div
      className={cn(WORKSPACE_LIST_PAGE.TABLE_SCROLL, "w-full min-w-0 overflow-hidden", className)}
      role="status"
      aria-busy="true"
      data-testid={testId}
    >
      <table className="w-full min-w-0 table-fixed border-collapse text-xs">
        <thead>
          <tr className={LIST_TABLE_THEAD_ROW_MUTED}>
            {Array.from({ length: safeColumns }, (_, col) => (
              <th key={`h-${col}`} className={cn(LIST_TABLE_TH, "font-medium")}>
                <Skeleton className="h-3 w-[72%]" style={{ animationDelay: `${col * 30}ms` }} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: safeRows }, (_, row) => (
            <tr key={`r-${row}`} className="border-b border-border/60">
              {Array.from({ length: safeColumns }, (_, col) => (
                <td key={`c-${row}-${col}`} className={LIST_TABLE_CELL}>
                  <Skeleton
                    className={cn("h-3", col === 0 ? "w-[88%]" : "w-[64%]")}
                    style={{ animationDelay: `${40 + row * 35 + col * 20}ms` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <span className="sr-only">Loading list</span>
    </div>
  );
}

export type HubListChromeSkeletonProps = {
  className?: string;
  testId?: string;
  rows?: number;
  columns?: number;
};

/** Suspense content fallback: FILTER_BAND + table stack (lean bank density). */
export function HubListChromeSkeleton({
  className,
  testId = "hub-list-chrome-skeleton",
  rows = DEFAULT_ROW_COUNT,
  columns = DEFAULT_COLUMN_COUNT,
}: HubListChromeSkeletonProps) {
  return (
    <div className={cn(WORKSPACE_LIST_PAGE.FILTER_SECTION, className)} data-testid={testId}>
      <div className={WORKSPACE_LIST_PAGE.FILTER_BAND} aria-hidden>
        <div className={WORKSPACE_LIST_PAGE.FILTER_ROW}>
          <Skeleton className="h-8 w-44 shrink-0 rounded-md" />
          <Skeleton className="h-8 w-28 shrink-0 rounded-md" style={{ animationDelay: "40ms" }} />
          <Skeleton className="h-8 w-24 shrink-0 rounded-md" style={{ animationDelay: "80ms" }} />
          <Skeleton className="ml-auto h-8 w-20 shrink-0 rounded-md" style={{ animationDelay: "120ms" }} />
        </div>
      </div>
      <div className={WORKSPACE_LIST_PAGE.TABLE_LIST_STACK}>
        <ListTableSkeleton rows={rows} columns={columns} />
      </div>
    </div>
  );
}
